import WebAppAPI from '@/utils/WebAppAPI';
import {
  CreateIntakePayload,
  UpdateIntakePayload,
  UpdateIntakeStatusPayload,
  TransferIntakePayload,
  AssignIntakePayload,
  AddIntakeCommentPayload,
  UpdateIntakeCommentPayload,
  AddIntakeLinkPayload,
  UpdateIntakeLinkPayload,
  Intake,
  IntakeComment,
  IntakeLink,
  IntakeBoardResponse,
} from '@/types';

interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  priority?: string;
  assignee?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
}

const urls = {
  createIntake: (team_id: string) => `/intakes/create-team-intake/${team_id}`,
  getTeamIntakes: (team_id: string, params?: PaginationParams) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.search?.trim()) query.append('search', params.search.trim());
    if (params?.status) query.append('status', params.status);
    if (params?.priority) query.append('priority', params.priority);
    if (params?.assignee) query.append('assignee', params.assignee);
    if (params?.sort_by) query.append('sort_by', params.sort_by);
    if (params?.order) query.append('order', params.order);
    const queryString = query.toString();
    return `/intakes/team-intakes/${team_id}${queryString ? `?${queryString}` : ''}`;
  },
  getUserIntakes: (email: string, page: number = 1) =>
    `/intakes/user-intakes/${encodeURIComponent(email)}?page=${page}`,
  getIntakesGroupedByStatus: (team_id: string) =>
    `/intakes/team-board/${team_id}`,
  getIntakeById: (intake_id: string) => `/intakes/${intake_id}`,
  updateIntake: (intake_id: string) => `/intakes/update/${intake_id}`,
  deleteIntake: (intake_id: string) => `/intakes/${intake_id}`,
  transferIntake: (intake_id: string) => `/intakes/${intake_id}/team`,
  transferIntakeToTeam: (intake_id: string) => `/intakes/${intake_id}/team`,
  assignIntake: (intake_id: string) => `/intakes/${intake_id}/assign`,
  unassignIntake: (intake_id: string) => `/intakes/${intake_id}/unassign`,
  getIntakeComments: (intake_id: string) =>
    `/intakes/${intake_id}/intake_comment`,
  addIntakeComment: (intake_id: string) =>
    `/intakes/${intake_id}/intake_comment`,
  updateIntakeComment: (intake_id: string, comment_id: string) =>
    `/intakes/${intake_id}/intake_comment/${comment_id}`,
  deleteIntakeComment: (intake_id: string, comment_id: string) =>
    `/intakes/${intake_id}/intake_comment/${comment_id}`,
  getIntakeLinks: (intake_id: string) => `/intakes/${intake_id}/intake_link`,
  addIntakeLink: (intake_id: string) => `/intakes/${intake_id}/intake_link`,
  deleteIntakeLink: (intake_id: string, link_id: string) =>
    `/intakes/${intake_id}/intake_link/${link_id}`,
  updateIntakeLink: (intake_id: string, link_id: string) =>
    `/intakes/${intake_id}/intake_link/${link_id}`,
};

const IntakeService = {
  createIntake: async (
    payload: CreateIntakePayload,
    team_id: string
  ): Promise<Intake> => {
    try {
      const response = await WebAppAPI().post(
        urls.createIntake(team_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getIntakes: async (
    team_id: string,
    params?: PaginationParams
  ): Promise<{ data: Intake[]; count: number; totalPages?: number }> => {
    try {
      const { data } = await WebAppAPI().get(
        urls.getTeamIntakes(team_id, params)
      );
      const meta = data?.meta;
      return {
        data: data?.data || [],
        count: meta?.total ?? data?.count ?? 0,
        totalPages: meta?.pages ?? meta?.totalPages,
      };
    } catch (error) {
      throw error;
    }
  },

  getUserIntakes: async (email: string, page: number = 1): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(urls.getUserIntakes(email, page));
      return data;
    } catch (error) {
      throw error;
    }
  },

  getIntakesGroupedByStatus: async (
    team_id: string
  ): Promise<IntakeBoardResponse> => {
    try {
      const response = await WebAppAPI().get(
        urls.getIntakesGroupedByStatus(team_id)
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  getIntakeById: async (intake_id: string): Promise<Intake> => {
    try {
      const { data } = await WebAppAPI().get(urls.getIntakeById(intake_id));
      return data?.data;
    } catch (error) {
      throw error;
    }
  },

  updateIntake: async (
    intake_id: string,
    payload: UpdateIntakePayload
  ): Promise<Intake> => {
    try {
      const response = await WebAppAPI().put(
        urls.updateIntake(intake_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  updateIntakeStatus: async (
    intake_id: string,
    payload: UpdateIntakeStatusPayload
  ): Promise<Intake> => {
    try {
      const response = await WebAppAPI().put(
        urls.updateIntake(intake_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteIntake: async (intake_id: string): Promise<void> => {
    try {
      await WebAppAPI().delete(urls.deleteIntake(intake_id));
    } catch (error) {
      throw error;
    }
  },

  transferIntake: async (
    intake_id: string,
    payload: TransferIntakePayload
  ): Promise<Intake> => {
    try {
      const response = await WebAppAPI().put(
        urls.transferIntake(intake_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  transferIntakeToTeam: async (
    intake_id: string,
    payload: TransferIntakePayload
  ): Promise<Intake> => {
    try {
      const response = await WebAppAPI().put(
        urls.transferIntakeToTeam(intake_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  assignIntake: async (
    intake_id: string,
    payload: AssignIntakePayload
  ): Promise<Intake> => {
    try {
      const { data } = await WebAppAPI().post(
        urls.assignIntake(intake_id),
        payload
      );
      return data?.data ?? data;
    } catch (error) {
      throw error;
    }
  },

  unassignIntake: async (intake_id: string): Promise<Intake> => {
    try {
      const { data } = await WebAppAPI().put(urls.unassignIntake(intake_id));
      return data?.data ?? data;
    } catch (error) {
      throw error;
    }
  },

  getIntakeComments: async (
    intake_id: string,
    params?: { page?: number; limit?: number }
  ): Promise<any> => {
    try {
      const { data } = await WebAppAPI().get(urls.getIntakeComments(intake_id), { params });
      return data;
    } catch (error) {
      throw error;
    }
  },

  addIntakeComment: async (
    intake_id: string,
    payload: AddIntakeCommentPayload
  ): Promise<IntakeComment> => {
    try {
      const response = await WebAppAPI().post(
        urls.addIntakeComment(intake_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  updateIntakeComment: async (
    intake_id: string,
    comment_id: string,
    payload: UpdateIntakeCommentPayload
  ): Promise<IntakeComment> => {
    try {
      const response = await WebAppAPI().put(
        urls.updateIntakeComment(intake_id, comment_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteIntakeComment: async (
    intake_id: string,
    comment_id: string
  ): Promise<void> => {
    try {
      await WebAppAPI().delete(urls.deleteIntakeComment(intake_id, comment_id));
    } catch (error) {
      throw error;
    }
  },

  getIntakeLinks: async (intake_id: string): Promise<IntakeLink[]> => {
    try {
      const { data } = await WebAppAPI().get(urls.getIntakeLinks(intake_id));
      return data?.data;
    } catch (error) {
      throw error;
    }
  },

  addIntakeLink: async (
    intake_id: string,
    payload: AddIntakeLinkPayload
  ): Promise<IntakeLink> => {
    try {
      const { data } = await WebAppAPI().post(
        urls.addIntakeLink(intake_id),
        payload
      );
      return data?.data;
    } catch (error) {
      throw error;
    }
  },

  deleteIntakeLink: async (
    intake_id: string,
    link_id: string
  ): Promise<void> => {
    try {
      await WebAppAPI().delete(urls.deleteIntakeLink(intake_id, link_id));
    } catch (error) {
      throw error;
    }
  },

  updateIntakeLink: async (
    intake_id: string,
    link_id: string,
    payload: UpdateIntakeLinkPayload
  ): Promise<IntakeLink> => {
    try {
      const response = await WebAppAPI().put(
        urls.updateIntakeLink(intake_id, link_id),
        payload
      );
      return response?.data;
    } catch (error) {
      throw error;
    }
  },
};

export default IntakeService;
